<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useIdeStore } from '@/stores/ide.store'

import advanceCompilerDatabaseService from '@/services/ide/advanceCompilerDatabase.service'
import { useAdvanceCompilerDatabaseStore } from '@/stores/advanceCompilerDatabase.store'
import MessageIcon from '@/assets/images/ide/message.svg?component'

const ideStore = useIdeStore()
const advCompDbStore = useAdvanceCompilerDatabaseStore()

const databaseInfo = computed(() => {
  return advCompDbStore.databaseInfo
})

const isPodInitializedCompleted = computed(() => {
  return advCompDbStore.isPodInitializationCompleted
})

const hostName = computed(() => {
  return `${databaseInfo.value?.subdomain}${databaseInfo.value?.domain}`
})

const dbUserName = computed(() => {
  return databaseInfo.value?.env?.user
})

const dbPassword = computed(() => {
  return databaseInfo.value?.env?.password
})

const dbPort = computed(() => {
  return databaseInfo?.value?.port
})

/**
 * Asynchronously fetches the database information for the current project language
 * by sending a request to the `advanceCompilerDatabaseService`.
 */
const fetchDataBaseInfo = async () => {
  const projectLanguage = ideStore.isLanguage as string
  const request = {
    language: `${projectLanguage}`
  }
  if (isPodInitializedCompleted.value) {
    const result = await advanceCompilerDatabaseService.checkDatabaseInfo(request)
    advCompDbStore.setDatabaseInfo(result.pod)

    if (dbUserName.value && hostName.value) {
      advCompDbStore.databaseInstanceName = `${dbUserName.value}@${hostName.value}`
    }
  }
}

onMounted(() => {
  fetchDataBaseInfo()
})

watch(
  () => isPodInitializedCompleted.value,
  (value) => {
    if (value) {
      fetchDataBaseInfo()
    }
  }
)
</script>

<template>
  <div class="background-primary flex h-full w-full flex-col">
    <div class="flex w-full flex-row justify-between p-2">
      <div class="flex flex-row gap-2">
        <MessageIcon class="icon-ide-emphasized h-4 w-4" />
        <span class="text-xs font-medium leading-snug"> Database info </span>
      </div>
    </div>
    <div
      v-if="databaseInfo"
      class="flex h-full w-full flex-col justify-between gap-2 overflow-y-scroll p-2"
    >
      <div class="text-high text-xs font-medium leading-tight">Host: {{ hostName }}</div>
      <div class="text-high text-xs font-medium leading-tight">Port: {{ dbPort }}</div>
      <div class="text-high text-xs font-medium leading-tight">Username : {{ dbUserName }}</div>
      <div class="text-high text-xs font-medium leading-tight">Password : {{ dbPassword }}</div>
    </div>
    <div v-else class="flex w-full flex-col justify-between gap-2 p-2">
      <div class="text-high flex justify-center text-xs font-medium leading-tight">
        Loading Info...
      </div>
    </div>
  </div>
</template>
